/**
 * SoleadLab 2.0 - API Config Modal
 *
 * Configure SCADA data provider credentials with dynamic form rendering.
 * Forms are generated from provider.credentialFields (self-describing).
 *
 * Admin-only access for credential management.
 */

import { useState, useEffect, useMemo } from 'react';
import Modal from '@/components/common/Modal';
import Button from '@/components/common/Button';
import { useMonitoringStore } from '@/stores';
import * as api from '@/services/monitoringApi';
import type { CredentialField, ProviderInfo } from '@/types/monitoring';
import './ApiConfigModal.css';

interface ApiConfigModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export default function ApiConfigModal({ isOpen, onClose }: ApiConfigModalProps) {
  const selectedSiteId = useMonitoringStore((s) => s.selectedSiteId);
  const credentialStatus = useMonitoringStore((s) => s.credentialStatus);
  const providerStatus = useMonitoringStore((s) => s.providerStatus);
  const providers = useMonitoringStore((s) => s.providers);
  const providerHealth = useMonitoringStore((s) => s.providerHealth);
  const setDataMode = useMonitoringStore((s) => s.setDataMode);
  const loadCredentialStatus = useMonitoringStore((s) => s.loadCredentialStatus);
  const loadProviders = useMonitoringStore((s) => s.loadProviders);

  // Form state — dynamic fields keyed by CredentialField.key
  const [providerName, setProviderName] = useState('');
  const [fieldValues, setFieldValues] = useState<Record<string, string>>({});

  // UI state
  const [isSaving, setIsSaving] = useState(false);
  const [isTesting, setIsTesting] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [testResult, setTestResult] = useState<{ ok: boolean; message: string } | null>(null);
  const [saveSuccess, setSaveSuccess] = useState(false);

  // Selected provider metadata
  const selectedProvider: ProviderInfo | undefined = useMemo(
    () => providers.find((p) => p.name === providerName),
    [providers, providerName]
  );

  const credentialFields: CredentialField[] = selectedProvider?.credentialFields ?? [];

  // Load providers and credential status when opened
  useEffect(() => {
    if (isOpen) {
      loadProviders();
      if (selectedSiteId) {
        loadCredentialStatus(selectedSiteId);
      }
      setError(null);
      setTestResult(null);
      setSaveSuccess(false);
      setFieldValues({});
    }
  }, [isOpen, selectedSiteId]);

  // Pre-fill provider name from existing credentials
  useEffect(() => {
    if (credentialStatus?.provider) {
      setProviderName(credentialStatus.provider);
    }
  }, [credentialStatus]);

  // Reset field defaults when provider changes
  useEffect(() => {
    if (selectedProvider) {
      const defaults: Record<string, string> = {};
      for (const field of selectedProvider.credentialFields) {
        if (field.defaultValue) {
          defaults[field.key] = field.defaultValue;
        }
      }
      setFieldValues(defaults);
    }
  }, [selectedProvider]);

  const setFieldValue = (key: string, value: string) => {
    setFieldValues((prev) => ({ ...prev, [key]: value }));
  };

  const validateFields = (): string | null => {
    for (const field of credentialFields) {
      const val = fieldValues[field.key]?.trim() ?? '';
      if (field.required && !val) {
        return `${field.label} is required`;
      }
      if (val && field.validation?.pattern) {
        const regex = new RegExp(field.validation.pattern);
        if (!regex.test(val)) {
          return field.validation.message ?? `${field.label} format is invalid`;
        }
      }
      if (val && field.validation?.minLength && val.length < field.validation.minLength) {
        return `${field.label} must be at least ${field.validation.minLength} characters`;
      }
    }
    return null;
  };

  const handleSave = async (): Promise<boolean> => {
    if (!selectedSiteId) return false;
    if (!providerName.trim()) {
      setError('Provider name is required');
      return false;
    }

    // Validate dynamic fields
    const validationError = validateFields();
    if (validationError) {
      setError(validationError);
      return false;
    }

    // Build credential object from field mapTo declarations
    const credentials: Record<string, unknown> = {
      provider_name: providerName.trim(),
    };

    for (const field of credentialFields) {
      const val = fieldValues[field.key]?.trim();
      if (!val) continue;

      if (field.mapTo === 'extra_config') {
        const existing = (credentials.extra_config ?? {}) as Record<string, unknown>;
        existing[field.key] = val;
        credentials.extra_config = existing;
      } else {
        credentials[field.mapTo] = val;
      }
    }

    // Fallback: if no fields mapped to api_key/api_secret via declarations,
    // check raw field values (for providers without credentialFields)
    if (!credentials.api_key && fieldValues['api_key']) {
      credentials.api_key = fieldValues['api_key'].trim();
    }
    if (!credentials.api_base_url && fieldValues['api_base_url']) {
      credentials.api_base_url = fieldValues['api_base_url'].trim();
    }
    if (!credentials.system_id && fieldValues['system_id']) {
      credentials.system_id = fieldValues['system_id'].trim();
    }

    // Inject provider's default base URL if not set
    if (!credentials.api_base_url && selectedProvider?.defaultBaseUrl) {
      credentials.api_base_url = selectedProvider.defaultBaseUrl;
    }

    if (!credentials.api_key && !credentials.api_secret) {
      setError('At least one credential (API key or secret) is required');
      return false;
    }

    setIsSaving(true);
    setError(null);
    setSaveSuccess(false);
    try {
      await api.setCredentials(selectedSiteId, credentials as Parameters<typeof api.setCredentials>[1]);
      await loadCredentialStatus(selectedSiteId);
      setSaveSuccess(true);
      // Clear sensitive fields after save
      const cleared: Record<string, string> = {};
      for (const [key, val] of Object.entries(fieldValues)) {
        const field = credentialFields.find((f) => f.key === key);
        if (field?.type !== 'password') {
          cleared[key] = val;
        }
      }
      setFieldValues(cleared);
      setTimeout(() => setSaveSuccess(false), 3000);
      return true;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save credentials');
      return false;
    } finally {
      setIsSaving(false);
    }
  };

  const handleTestConnection = async () => {
    if (!selectedSiteId) return;

    setIsTesting(true);
    setTestResult(null);
    setError(null);
    try {
      const result = await api.testConnection(selectedSiteId, providerName || undefined);
      setTestResult(result);
    } catch (err) {
      setTestResult({
        ok: false,
        message: err instanceof Error ? err.message : 'Connection test failed',
      });
    } finally {
      setIsTesting(false);
    }
  };

  const handleSaveAndTest = async () => {
    const saved = await handleSave();
    if (saved) {
      await handleTestConnection();
    }
  };

  const handleDeleteCredentials = async () => {
    if (!selectedSiteId || !credentialStatus?.provider) return;
    if (!confirm('Delete stored credentials? This will disconnect the live data feed.')) return;

    setIsDeleting(true);
    setError(null);
    try {
      await api.deleteCredentials(selectedSiteId, credentialStatus.provider);
      await loadCredentialStatus(selectedSiteId);
      setProviderName('');
      setFieldValues({});
      setDataMode('upload');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete credentials');
    } finally {
      setIsDeleting(false);
    }
  };

  const hasCredentials = credentialStatus?.hasCredentials ?? false;
  const isGeneric = providerName === 'generic';
  const hasRequiredValues = credentialFields.length > 0
    ? credentialFields.filter((f) => f.required).every((f) => fieldValues[f.key]?.trim())
    : !!(fieldValues['api_key']?.trim());

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="API Credentials"
      size="md"
      footer={
        <div className="acm-footer">
          <Button variant="ghost" onClick={onClose}>Close</Button>
          {hasCredentials ? (
            <>
              <Button
                variant="secondary"
                onClick={handleSave}
                disabled={isSaving || isTesting || !providerName || !hasRequiredValues}
                loading={isSaving}
              >
                Update Credentials
              </Button>
              <Button
                variant="primary"
                onClick={handleTestConnection}
                disabled={isTesting || isSaving}
                loading={isTesting}
              >
                Test Connection
              </Button>
            </>
          ) : (
            <Button
              variant="primary"
              onClick={handleSaveAndTest}
              disabled={isSaving || isTesting || !providerName || !hasRequiredValues}
              loading={isSaving || isTesting}
            >
              {isTesting ? 'Testing...' : 'Save & Test Connection'}
            </Button>
          )}
        </div>
      }
    >
      <form className="acm-content" autoComplete="off" onSubmit={(e) => e.preventDefault()}>
        {/* Hidden decoy fields to absorb browser autofill */}
        <input type="text" name="decoy-user" autoComplete="username" style={{ display: 'none' }} tabIndex={-1} aria-hidden="true" />
        <input type="password" name="decoy-pass" autoComplete="current-password" style={{ display: 'none' }} tabIndex={-1} aria-hidden="true" />
        {/* Generic adapter warning */}
        {isGeneric && (
          <div className="acm-warning-banner">
            Limited reliability provider — schema binding and alarm results are not guaranteed
          </div>
        )}

        {/* Credential Status */}
        {hasCredentials && (
          <div className="acm-status">
            <div className="acm-status__header">
              <span className={`acm-status__dot acm-status__dot--${providerStatus === 'connected' ? 'ok' : providerStatus === 'error' ? 'error' : 'off'}`} />
              <span className="acm-status__text">
                Credentials stored for <strong>{credentialStatus?.provider}</strong>
              </span>
            </div>
            {credentialStatus?.createdAt && (
              <div className="acm-status__meta">
                Saved: {new Date(credentialStatus.createdAt).toLocaleDateString()}
              </div>
            )}
            {/* Health summary from DO */}
            {providerHealth && providerHealth.status !== 'stopped' && (
              <div className={`acm-health acm-health--${providerHealth.status}`}>
                <span className="acm-health__label">Status:</span>
                <span className="acm-health__value">{providerHealth.status}</span>
                {providerHealth.consecutiveFailures > 0 && (
                  <span className="acm-health__failures">
                    ({providerHealth.consecutiveFailures} failures)
                  </span>
                )}
                {providerHealth.message && (
                  <div className="acm-health__message">{providerHealth.message}</div>
                )}
              </div>
            )}
          </div>
        )}

        {/* Provider Selection */}
        <div className="acm-field">
          <label className="acm-field__label">Provider</label>
          <select
            className="acm-field__select"
            value={providerName}
            onChange={(e) => setProviderName(e.target.value)}
          >
            <option value="">-- Select Provider --</option>
            {providers.length > 0 ? (
              providers.map((p) => (
                <option key={p.name} value={p.name}>
                  {p.displayName}
                  {p.capabilities && !p.capabilities.productionReady ? ' (Beta)' : ''}
                </option>
              ))
            ) : (
              <>
                <option value="solaredge">SolarEdge</option>
                <option value="meteocontrol">Meteocontrol</option>
                <option value="generic">Generic / Custom (Beta)</option>
              </>
            )}
          </select>
          {selectedProvider?.documentationUrl && (
            <a
              href={selectedProvider.documentationUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="acm-field__doclink"
            >
              API Documentation &rarr;
            </a>
          )}
        </div>

        {/* Dynamic Credential Fields (from provider.credentialFields) */}
        {credentialFields.map((field) => (
          <div key={field.key} className="acm-field">
            <label className="acm-field__label">
              {field.label}
              {!field.required && <span className="acm-field__optional">(optional)</span>}
              {hasCredentials && field.type === 'password' && (
                <span className="acm-field__hint">(enter new value to update)</span>
              )}
            </label>
            {field.type === 'select' && field.options ? (
              <select
                className="acm-field__select"
                value={fieldValues[field.key] ?? field.defaultValue ?? ''}
                onChange={(e) => setFieldValue(field.key, e.target.value)}
              >
                <option value="">-- Select --</option>
                {field.options.map((opt) => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            ) : (
              <input
                className="acm-field__input"
                type={field.type === 'password' ? 'password' : field.type === 'url' ? 'url' : 'text'}
                value={fieldValues[field.key] ?? ''}
                onChange={(e) => setFieldValue(field.key, e.target.value)}
                placeholder={hasCredentials && field.type === 'password'
                  ? '\u2022\u2022\u2022\u2022\u2022\u2022\u2022\u2022\u2022\u2022'
                  : field.placeholder ?? ''}
                autoComplete={field.type === 'password' ? 'new-password' : 'one-time-code'}
                name={`acm-${field.key}-${providerName}`}
              />
            )}
            {field.description && (
              <div className="acm-field__description">{field.description}</div>
            )}
          </div>
        ))}

        {/* Fallback: static fields for providers without credentialFields */}
        {providerName && credentialFields.length === 0 && (
          <>
            <div className="acm-field">
              <label className="acm-field__label">API Base URL <span className="acm-field__optional">(optional)</span></label>
              <input
                className="acm-field__input"
                type="url"
                value={fieldValues['api_base_url'] ?? ''}
                onChange={(e) => setFieldValue('api_base_url', e.target.value)}
                placeholder="https://api.provider.com/v2"
              />
            </div>
            <div className="acm-field">
              <label className="acm-field__label">
                API Key
                {hasCredentials && <span className="acm-field__hint">(enter new key to update)</span>}
              </label>
              <input
                className="acm-field__input"
                type="password"
                value={fieldValues['api_key'] ?? ''}
                onChange={(e) => setFieldValue('api_key', e.target.value)}
                placeholder={hasCredentials ? '\u2022\u2022\u2022\u2022\u2022\u2022\u2022\u2022\u2022\u2022' : 'Enter API key'}
                autoComplete="new-password"
                name="acm-fallback-api-key"
              />
            </div>
            <div className="acm-field">
              <label className="acm-field__label">System / Plant ID <span className="acm-field__optional">(optional)</span></label>
              <input
                className="acm-field__input"
                type="text"
                value={fieldValues['system_id'] ?? ''}
                onChange={(e) => setFieldValue('system_id', e.target.value)}
                placeholder="e.g., ABCDE"
              />
            </div>
          </>
        )}

        {/* Rate Limits Info */}
        {selectedProvider?.rateLimits?.description && (
          <div className="acm-ratelimit">
            {selectedProvider.rateLimits.description}
          </div>
        )}

        {/* SolarEdge budget display */}
        {providerHealth?.dailyCallLimit && (
          <div className={`acm-budget ${
            (providerHealth.dailyCallCount ?? 0) > providerHealth.dailyCallLimit * 0.8
              ? 'acm-budget--warning' : ''
          }`}>
            <span>API Budget: {providerHealth.dailyCallCount ?? 0} / {providerHealth.dailyCallLimit} calls today</span>
            <div className="acm-budget__bar">
              <div
                className="acm-budget__fill"
                style={{ width: `${Math.min(100, ((providerHealth.dailyCallCount ?? 0) / providerHealth.dailyCallLimit) * 100)}%` }}
              />
            </div>
          </div>
        )}

        {/* Delete Credentials */}
        {hasCredentials && (
          <div className="acm-actions">
            <Button
              variant="danger"
              size="sm"
              onClick={handleDeleteCredentials}
              disabled={isDeleting}
              loading={isDeleting}
            >
              Delete Credentials
            </Button>
          </div>
        )}

        {/* Test Result */}
        {testResult && (
          <div className={`acm-test-result ${testResult.ok ? 'acm-test-result--ok' : 'acm-test-result--fail'}`}>
            <span className="acm-test-result__icon">
              {testResult.ok ? '\u2713' : '\u2717'}
            </span>
            <span>{testResult.message}</span>
          </div>
        )}

        {/* Error */}
        {error && (
          <div className="acm-error">
            <span className="acm-error__icon">!</span>
            <span>{error}</span>
          </div>
        )}

        {/* Save Success */}
        {saveSuccess && (
          <div className="acm-success">
            <span>{'\u2713'}</span>
            <span>Credentials saved and encrypted successfully</span>
          </div>
        )}
      </form>
    </Modal>
  );
}
